import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiDownload, apiFetchJson, fetchAllPages, newIdempotencyKey } from "../apiClient";
import { useAuth } from "../AuthContext";
import { statusBadgeClass } from "../utils/statusBadge";
import { resolvePollMs } from "../utils/pollInterval";
import { applyRunEvent, LIVE_RUN_STATUSES as LIVE } from "../utils/runEvents";
import useRunEvents from "../hooks/useRunEvents";
import usePagedList from "../hooks/usePagedList";
import ApprovalPanel from "./ApprovalPanel";
import { CheckRow } from "./CapabilityCards";
import TeamPanel from "./TeamPanel";
import "./AdminCollections.css";

const POLL_MS = resolvePollMs(
  (window._env_ && window._env_.REACT_APP_AGENT_POLL_MS) || process.env.REACT_APP_AGENT_POLL_MS,
  (window._env_ && window._env_.REACT_APP_AGENT_POLL_MIN_MS) || process.env.REACT_APP_AGENT_POLL_MIN_MS,
);

const EXPORT_FORMATS = ["docx", "pdf", "xlsx"];

const withDatabase = (path, database) =>
  database ? `${path}${path.includes("?") ? "&" : "?"}database=${encodeURIComponent(database)}` : path;

/** Merge a refreshed first page into the loaded list: updated runs replace their old copy,
 * new runs go on top, runs from later pages stay. */
function mergeRuns(loaded, fresh) {
  const byId = new Map(fresh.map((r) => [r.id, r]));
  const kept = loaded.filter((r) => !byId.has(r.id));
  return [...fresh, ...kept];
}

function Steps({ steps }) {
  const { t } = useTranslation();
  return (
    <ol className="agent-steps">
      {(steps || []).map((s, i) => (
        <li key={i}>
          <strong>{t(`agents.stepType_${s.type}`, { defaultValue: s.type })}</strong>
          {s.tool && <> · <span className="ds-mono">{s.tool}</span></>}
          {s.decision && <> · {t(`agents.decision_${s.decision}`, { defaultValue: s.decision })} · {s.by}</>}
          {s.interrupted && <> · <span className="ds-badge ds-badge--warning">{t("agents.outcomeUnknown")}</span></>}
          {s.detail && <> · {s.detail}</>}
          {s.observation && <pre>{s.observation}</pre>}
        </li>
      ))}
    </ol>
  );
}

/** The sub-agent runs of a run tree (S3): who did what for this run, newest first. Re-read
 * whenever the parent's status changes (a sub-agent finishing wakes the parent). */
function SubRuns({ rootId, parentStatus }) {
  const { t } = useTranslation();
  const { items, hasMore, loading, error, loadMore, reload } =
    usePagedList(`/api/v1/agents/runs?root_run_id=${encodeURIComponent(rootId)}`);
  useEffect(() => { reload(); }, [parentStatus, reload]);
  if (!items.length && !error) return null;
  return (
    <div className="ac-detail-section">
      <h4 className="ac-detail-heading">{t("agents.subAgentRuns")}</h4>
      {error && <div className="ds-alert ds-alert--danger" role="alert">{error}</div>}
      <ul className="ds-list">
        {items.map((child) => (
          <li key={child.id} className="ds-list-row">
            <span className={statusBadgeClass(child.status)}>{t(`agents.status_${child.status}`)}</span>
            <span className="ds-meta">{child.agent_name} · {t("agents.depth", { depth: child.depth })}</span>
            <span className="ds-list-title">{child.task}</span>
            {child.result && <p>{child.result}</p>}
            {child.error && <div className="ds-alert ds-alert--danger">{child.error}</div>}
          </li>
        ))}
      </ul>
      {hasMore && (
        <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm" disabled={loading} onClick={loadMore}>
          {t("agents.loadMore")}
        </button>
      )}
    </div>
  );
}

/** Runs of the Agents tab: start a run, follow its steps, and approve or reject the exact
 * pending action. Who may decide is enforced by the server; the UI mirrors it. */
export default function AgentRuns({ database, startAgent = null, onStarted = () => {} }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState(startAgent ? startAgent.id : "");
  const [task, setTask] = useState("");
  const [runs, setRuns] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [reasons, setReasons] = useState({});
  const [dryRun, setDryRun] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [startKey, setStartKey] = useState(newIdempotencyKey);
  const epoch = useRef(0);            // bumped on unmount only: responses of a dead mount are dropped
  const firstPageLoaded = useRef(false);
  const queue = useRef(Promise.resolve());   // refreshes run one at a time, so none overtakes another
  const pending = useRef(0);
  const runsRef = useRef(runs);
  runsRef.current = runs;

  const refreshOnce = useCallback(async () => {
    const mount = epoch.current;
    try {
      const data = await apiFetchJson("/api/v1/agents/runs");
      if (mount !== epoch.current) return;
      const fresh = data.items || [];
      setRuns((prev) => mergeRuns(prev, fresh));
      // Only the first load sets the cursor; later polls must not resurrect "Load more"
      // after the last page was loaded.
      if (!firstPageLoaded.current) {
        firstPageLoaded.current = true;
        setNextCursor(data.next_cursor || null);
      }
      // Live runs loaded through "Load more" are not on page 1: refresh those by id
      // (bounded by what is currently shown).
      const onFirstPage = new Set(fresh.map((r) => r.id));
      const stale = runsRef.current.filter((r) => LIVE.has(r.status) && !onFirstPage.has(r.id));
      const results = await Promise.allSettled(stale.map((r) => apiFetchJson(`/api/v1/agents/runs/${r.id}`)));
      if (mount !== epoch.current) return;
      const updated = new Map();
      results.forEach((res) => {
        if (res.status === "fulfilled") updated.set(res.value.id, res.value);
        else if (res.reason && res.reason.status !== 404) setError(res.reason.message);
      });
      if (updated.size) setRuns((prev) => prev.map((r) => updated.get(r.id) || r));
    } catch (e) {
      if (mount === epoch.current) setError(e.message);
    }
  }, []);

  const refresh = useCallback(() => {
    pending.current += 1;
    const done = queue.current.then(refreshOnce).finally(() => { pending.current -= 1; });
    queue.current = done;
    return done;
  }, [refreshOnce]);

  useEffect(() => {
    refresh();
    return () => { epoch.current += 1; };   // unmount / StrictMode remount: drop in-flight responses
  }, [refresh]);

  useEffect(() => {
    let cancelled = false;
    fetchAllPages("/api/v1/agents?scope=all")
      .then((items) => { if (!cancelled) setAgents(items.filter((a) => a.can_run)); })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, []);

  // The selection follows the library's "Start"; without one it falls back to a listed agent,
  // so the select never points at a missing option.
  useEffect(() => {
    if (startAgent) {
      setAgentId(startAgent.id);
    } else {
      setAgentId((id) => (agents.some((a) => a.id === id) ? id : (agents.length ? agents[0].id : "")));
    }
  }, [startAgent, agents]);
  useEffect(() => { setStartKey(newIdempotencyKey()); }, [agentId]);   // a different agent is a different action

  const agentOptions = startAgent && !agents.some((a) => a.id === startAgent.id) ? [startAgent, ...agents] : agents;

  // The opened live run follows its event stream (steps appear as they happen); the poll
  // below keeps the rest of the list current and is the stream's safety net.
  const openRun = runs.find((r) => r.id === openId);
  const onRunEvent = useCallback((event) => {
    setRuns((prev) => prev.map((r) => (r.id === event.run_id ? applyRunEvent(r, event) : r)));
  }, []);
  useRunEvents(openId, { enabled: Boolean(openRun && LIVE.has(openRun.status)), onEvent: onRunEvent });

  const anyLive = runs.some((r) => LIVE.has(r.status));
  useEffect(() => {
    if (!anyLive) return undefined;
    // A tick is a no-op while a refresh is still running, so a slow server never gets overlapping polls.
    const id = setInterval(() => { if (pending.current === 0) refresh(); }, POLL_MS);
    return () => clearInterval(id);
  }, [anyLive, refresh]);

  const act = async (request) => {
    setBusy(true);
    try {
      await request();
      setError("");
      await refresh();
    } catch (e) {
      setError(e.message);
      await refresh();     // e.g. 409 "expired": show the run's real state next to the message
    } finally {
      setBusy(false);
    }
  };

  const start = (e) => {
    e.preventDefault();
    if (!task.trim() || !agentId) return;
    act(async () => {
      let run;
      try {
        run = await apiFetchJson(withDatabase("/api/v1/agents/runs", database), {
          method: "POST", headers: { "Idempotency-Key": startKey },
          body: JSON.stringify({ agent_id: agentId, task, dry_run: dryRun }),
        });
      } catch (err) {
        // A server answer (e.g. 503 "could not be queued": the key now replays that failed run) is definitive,
        // so the retry is a new action. Only a network error (no status) keeps the key: the request may have landed.
        if (err && err.status) setStartKey(newIdempotencyKey());
        throw err;
      }
      setTask("");
      setStartKey(newIdempotencyKey());     // the next run is a new action
      setOpenId(run.id);
      onStarted();
    });
  };

  const exportRun = async (run, fmt) => {
    try {
      const lang = (i18n.language || "en").slice(0, 2) === "de" ? "de" : "en";
      await apiDownload(`/api/v1/agents/runs/${run.id}/export?format=${fmt}&lang=${lang}`, `agent-run-${run.id}.${fmt}`);
      setError("");
    } catch (e) {
      setError(e.message);
    }
  };

  const decide = (run, verb) => act(() => apiFetchJson(`/api/v1/agents/runs/${run.id}/${verb}`, {
    method: "POST", body: JSON.stringify({ reason: reasons[run.id] || "" }),
  }));

  const loadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const data = await apiFetchJson(`/api/v1/agents/runs?cursor=${encodeURIComponent(nextCursor)}`);
      setRuns((prev) => [...prev, ...(data.items || []).filter((r) => !prev.some((p) => p.id === r.id))]);
      setNextCursor(data.next_cursor || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="ds-page">
      <header className="ds-page-header">
        <h2 className="ds-page-title">{t("agents.runsTitle")}</h2>
      </header>
      {error && <div className="ds-alert ds-alert--danger" role="alert">{error}</div>}
      <form onSubmit={start} className="ds-card ac-form-stack" aria-label={t("agents.startRun")}>
        <div className="ds-field">
          <label className="ds-label" htmlFor="agent-run-agent">{t("agents.agentLabel")}</label>
          <select id="agent-run-agent" className="ds-select ac-control-wide" value={agentId}
                  onChange={(e) => setAgentId(e.target.value)}>
            {agentOptions.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
        <div className="ds-field">
          <label className="ds-label" htmlFor="agent-run-task">{t("agents.taskLabel")}</label>
          <textarea id="agent-run-task" className="ds-textarea" rows={3} placeholder={t("agents.taskPlaceholder")}
                    value={task} onChange={(e) => { setTask(e.target.value); setStartKey(newIdempotencyKey()); }} />
        </div>
        <CheckRow id="agent-run-dry" checked={dryRun} label={t("agents.dryRun")} help={t("agents.dryRunHelp")}
                  onChange={(e) => { setDryRun(e.target.checked); setStartKey(newIdempotencyKey()); }} />
        <div className="ac-actions">
          <button type="submit" className="ds-btn ds-btn--primary" disabled={busy || !agentId || !task.trim()}>
            {t("agents.startRun")}
          </button>
        </div>
      </form>

      {runs.length === 0 ? <div className="ds-empty">{t("agents.noRuns")}</div> : (
        <ul className="ds-list">
          {runs.map((r) => (
            <li key={r.id} className="ds-list-row">
              <button type="button" className="ds-list-toggle" aria-expanded={openId === r.id}
                      onClick={() => setOpenId(openId === r.id ? null : r.id)}>
                <span data-testid={`status-${r.id}`} className={statusBadgeClass(r.status)}>
                  {t(`agents.status_${r.status}`)}
                </span>
                <span className="ds-meta">{r.agent_name} {r.definition_version ? t("agents.version", { version: r.definition_version }) : ""}</span>
                {r.dry_run && <span className="ds-badge">{t("agents.dryRunBadge")}</span>}
                <span className="ds-list-title">{r.task}</span>
              </button>
              {openId === r.id && (
                <div className="ds-list-detail">
                  <Steps steps={r.steps} />
                  <SubRuns rootId={r.id} parentStatus={r.status} />
                  {r.team && <TeamPanel runId={r.id} version={(r.team_seq || 0) + (LIVE.has(r.status) ? 0 : 1)} />}
                  {r.result && (
                    <div className="agent-result">
                      <h4 className="ac-detail-heading">{t("agents.result")}</h4>
                      <p>{r.result}</p>
                    </div>
                  )}
                  {r.error && <div className="ds-alert ds-alert--danger">{r.error}</div>}
                  {(r.approvals || []).length > 0 && (
                    <div className="ac-detail-section">
                      <h4 className="ac-detail-heading">{t("agents.decisions")}</h4>
                      <ul>
                        {r.approvals.map((a, i) => (
                          <li key={i}>{t("agents.decisionLine", { decision: t(`agents.decision_${a.decision}`, { defaultValue: a.decision }), by: a.by, reason: a.reason || "–" })}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <ApprovalPanel run={r} user={user} busy={busy} reason={reasons[r.id]}
                                 onReason={(value) => setReasons((p) => ({ ...p, [r.id]: value }))}
                                 onDecide={(verb) => decide(r, verb)} />
                  {LIVE.has(r.status) && (
                    <div className="ac-actions">
                      <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm" disabled={busy}
                              onClick={() => act(() => apiFetchJson(`/api/v1/agents/runs/${r.id}/cancel`, { method: "POST" }))}>
                        {t("agents.cancelRun")}
                      </button>
                    </div>
                  )}
                  {!LIVE.has(r.status) && (
                    <div className="ac-actions" role="group" aria-label={t("agents.exportRun")}>
                      {EXPORT_FORMATS.map((fmt) => (
                        <button key={fmt} type="button" className="ds-btn ds-btn--secondary ds-btn--sm" disabled={busy}
                                onClick={() => exportRun(r, fmt)}>
                          {t(`agents.export_${fmt}`)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {nextCursor && (
        <button type="button" className="ds-btn ds-btn--secondary ac-load-more" disabled={loadingMore} onClick={loadMore}>
          {t("agents.loadMore")}
        </button>
      )}
    </div>
  );
}
